import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Image,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { PressableCard } from './PressableCard';
import { SkeletonCard } from './SkeletonLoader';
import { ChartComponent } from './ChartComponent';
import { apiService } from '../services/apiService';
import { CurrencyData } from '../types';

const { width } = Dimensions.get('window');
const isDesktop = width >= 1024;

// Responsive chart scale
const getChartScale = () => {
  if (width >= 1600) return 1.0;
  if (width >= 1280) return 0.85;
  return 0.75;
};

interface CurrencySectionProps {
  includeWarrants?: boolean;
  onCurrencyPress?: (currencyCode: string) => void;
  hideLegendDetails?: boolean;
  ownershipScope?: 'group' | 'self';
}

export const CurrencySection: React.FC<CurrencySectionProps> = React.memo(({
  includeWarrants = true,
  onCurrencyPress,
  hideLegendDetails = false,
  ownershipScope = 'group',
}) => {
  const [currencyData, setCurrencyData] = useState<CurrencyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Currency to country code mapping for flag URLs
  const currencyToCountry: { [key: string]: string } = {
    'USD': 'us',
    'EUR': 'eu', 
    'CHF': 'ch',
    'GBP': 'gb',
    'JPY': 'jp',
    'CAD': 'ca',
    'AUD': 'au',
    'SEK': 'se',
    'NOK': 'no',
    'DKK': 'dk',
    'PLN': 'pl',
    'CZK': 'cz',
    'HUF': 'hu',
    'RUB': 'ru',
    'CNY': 'cn',
    'INR': 'in',
    'BRL': 'br',
    'MXN': 'mx',
    'ZAR': 'za',
    'KRW': 'kr',
    'SGD': 'sg',
    'HKD': 'hk',
    'NZD': 'nz',
    'TRY': 'tr',
    'ILS': 'il'
  };

  const getFlagUrl = (currencyCode: string) => {
    const countryCode = currencyToCountry[currencyCode];
    if (!countryCode) return null;
    return `https://flagcdn.com/w40/${countryCode}.png`;
  };



  const fetchCurrencyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.getNotionalByCurrency(includeWarrants, ownershipScope);
      setCurrencyData(data);
    } catch (err) {
      console.error('Error fetching currency data:', err);
      setError('Error loading currency data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencyData();
  }, [includeWarrants, ownershipScope]);

  if (loading) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Notional per currency</Text>
        <SkeletonCard />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Notional per currency</Text>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </View>
    );
  }

  // Excluir monedas con 0% del gráfico y de la lista
  const currencyDataWithAllocation = currencyData.filter(item => item.allocation > 0);

  // Transform currency data to match ChartComponent format
  // allocation contains percentage
  const chartData = currencyDataWithAllocation.map(item => ({
    issuerName: item.currencyCode,
    numberOfProducts: item.numberOfProducts, // Notional value (used for proportions)
    allocation: item.allocation, // Percentage
    notionalOriginal: item.notionalOriginal,
  }));

  const formatNumberShort = (value: number) => {
    if (value >= 1e12) return (value / 1e12).toFixed(1) + ' B';
    if (value >= 1e9) return (value / 1e9).toFixed(1) + ' M';
    if (value >= 1e6) return (value / 1e6).toFixed(1) + ' m';
    if (value >= 1e3) return (value / 1e3).toFixed(1) + ' k';
    return value.toString();
  };

  return (
    <View style={isDesktop ? styles.sectionDesktop : styles.section}>
      <Text style={styles.sectionTitle}>Outstanding Notional by Currency</Text>
      
      {currencyDataWithAllocation.length > 0 ? (
        isDesktop ? (
          // Desktop: Show pie chart like Issuer Ranking
          <View style={styles.chartContainer}>
            <ChartComponent
              data={chartData}
              chartType="pie"
              variant="embedded"
              scale={getChartScale()}
              showAllData={false}
              showAllInChart={true}
              groupSmallItems={false}
              metric="percentage"
              onIssuerPress={(currencyCode) => onCurrencyPress?.(currencyCode)}
              hideLegendDetails={hideLegendDetails}
              showLegendAmountBelow
            />
          </View>
        ) : (
          // Mobile/Tablet: Keep list view
          <View style={styles.currencyList}>
            {currencyDataWithAllocation.map((item, index) => (
              <PressableCard 
                key={item.currencyCode} 
                style={[
                  styles.currencyItem,
                  index === currencyDataWithAllocation.length - 1 ? styles.currencyItemLast : null
                ].filter(Boolean) as any}
                onPress={() => onCurrencyPress?.(item.currencyCode)}
                pressedScale={0.98}
              >
                <View style={styles.currencyLeft}>
                  <View style={styles.flagContainer}>
                    {getFlagUrl(item.currencyCode) ? (
                      <Image
                        source={{ uri: getFlagUrl(item.currencyCode)! }}
                        style={styles.flagImage}
                        resizeMode="cover"
                      />
                    ) : (
                      <View style={styles.fallbackFlag}>
                        <Text style={styles.fallbackText}>
                          {item.currencyCode.substring(0, 2)}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.currencyInfo}>
                    <Text style={styles.currencyCode}>{item.currencyCode}</Text>
                    <Text style={styles.currencyName}>{item.currencyName}</Text>
                  </View>
                </View>
                <View style={styles.currencyRight}>
                  <Text style={styles.currencyPercentage}>
                    {item.allocation.toFixed(2)}%
                  </Text>
                  <Text style={styles.currencyAmount}>
                    {formatNumberShort(item.notionalOriginal ?? item.numberOfProducts)}
                  </Text>
                </View>
              </PressableCard>
            ))}
          </View>
        )
      ) : (
        <Text style={styles.noDataText}>No currency data available</Text>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionDesktop: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#F4F4F4',
    padding: width >= 1600 ? 24 : width >= 1280 ? 20 : 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: width >= 1600 ? 18 : width >= 1280 ? 16 : 15,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: width >= 1280 ? 16 : 12,
    fontFamily: 'Montserrat_700Bold',
  },
  chartContainer: {
    marginTop: 8,
  },
  currencyList: {
  },
  currencyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  currencyItemLast: {
    borderBottomWidth: 0,
  },
  currencyLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  flagContainer: {
    width: 40,
    height: 40,
    marginRight: 16,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    overflow: 'hidden',
  },
  flagImage: {
    width: 32,
    height: 24,
    borderRadius: 4,
  },
  fallbackFlag: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#6B7280',
  },
  fallbackText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: 'Montserrat_700Bold',
  },
  currencyInfo: {
    flex: 1,
  },
  currencyCode: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 2,
    fontFamily: 'Montserrat_700Bold',
  },
  currencyName: {
    fontSize: 14,
    color: '#9CA3AF',
    textTransform: 'capitalize',
    fontFamily: 'Montserrat_400Regular',
  },
  currencyPercentage: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    fontFamily: 'Montserrat_700Bold',
  },
  currencyRight: {
    alignItems: 'flex-end',
  },
  currencyAmount: {
    marginTop: 2,
    fontSize: 15,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  errorContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  noDataText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingVertical: 40,
    fontFamily: 'Montserrat_400Regular',
  },
});